import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../context/SettingsContext';

const usePageSEO = ({
  title,
  description,
  keywords,
  noIndex = false,
  canonicalPath,
  image,
  type = 'website',
  structuredData,
  defer = false,
} = {}) => {
  const { settings } = useSettings();
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.setAttribute('data-page-seo-active', 'true');

    const updateSEO = () => {
      if (defer) return;

      const lang = i18n.language?.substring(0, 2) || 'en';
      const isAr = lang === 'ar';
      const siteName = (isAr && settings.site_name_ar) ? settings.site_name_ar : (settings.site_name || 'Itronix Electronics');
      const defaultTitle = (isAr && settings.meta_title_ar) ? settings.meta_title_ar : (settings.meta_title || siteName);
      const defaultDesc = (isAr && settings.meta_description_ar) ? settings.meta_description_ar : settings.meta_description;
      const rawBaseUrl = String(settings.site_url || '').trim();
      const cleanBaseUrl = (rawBaseUrl && !['null', 'undefined'].includes(rawBaseUrl.toLowerCase()) ? rawBaseUrl : 'https://www.itronixbh.com')
        .replace(/\/+$/, '')
        .replace(/^(?!https?:\/\/)/, 'https://');
      const path = canonicalPath || window.location.pathname || '/';
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const basePageUrl = `${cleanBaseUrl}${normalizedPath}`;
      const canonicalUrl = isAr ? `${basePageUrl}?lang=ar` : basePageUrl;
      const pageTitle = title ? `${title} | ${siteName}` : defaultTitle;
      const pageDescription = description || defaultDesc || '';
      const rawImage = image || settings.logo_url || '/logo.png';
      const imageUrl = rawImage
        ? new URL(rawImage, `${cleanBaseUrl}/`).href
        : '';
      const imagePath = imageUrl.split('?', 1)[0].toLowerCase();
      const imageType = imagePath.endsWith('.png')
        ? 'image/png'
        : imagePath.endsWith('.webp')
          ? 'image/webp'
          : imagePath.endsWith('.gif')
            ? 'image/gif'
            : imageUrl
              ? 'image/jpeg'
              : '';

      document.title = pageTitle;

      const setMeta = (attr, key, content) => {
        let el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!content) {
          if (el) el.remove();
          return;
        }
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };

      const setNameMeta = (name, content) => setMeta('name', name, content);
      const setPropertyMeta = (property, content) => setMeta('property', property, content);

      setNameMeta('description', pageDescription);
      setNameMeta('keywords', keywords || '');
      setNameMeta('robots', noIndex ? 'noindex,follow' : 'index,follow');
      setPropertyMeta('og:type', type);
      setPropertyMeta('og:title', pageTitle);
      setPropertyMeta('og:description', pageDescription);
      setPropertyMeta('og:url', canonicalUrl);
      setPropertyMeta('og:site_name', siteName);
      setPropertyMeta('og:locale', isAr ? 'ar_BH' : 'en_BH');
      setPropertyMeta('og:image', imageUrl);
      setPropertyMeta('og:image:secure_url', imageUrl);
      setPropertyMeta('og:image:type', imageType);
      setPropertyMeta('og:image:alt', pageTitle);
      setNameMeta('twitter:card', imageUrl ? 'summary_large_image' : 'summary');
      setNameMeta('twitter:title', pageTitle);
      setNameMeta('twitter:description', pageDescription);
      setNameMeta('twitter:image', imageUrl);
      setNameMeta('twitter:image:alt', pageTitle);

      const setLink = (rel, href, hreflang) => {
        const selector = hreflang
          ? `link[rel="${rel}"][hreflang="${hreflang}"]`
          : `link[rel="${rel}"]`;
        let el = document.querySelector(selector);
        if (!el) {
          el = document.createElement('link');
          el.setAttribute('rel', rel);
          if (hreflang) el.setAttribute('hreflang', hreflang);
          document.head.appendChild(el);
        }
        el.setAttribute('href', href);
      };

      setLink('canonical', canonicalUrl);
      if (!noIndex) {
        setLink('alternate', basePageUrl, 'en');
        setLink('alternate', `${basePageUrl}?lang=ar`, 'ar');
        setLink('alternate', basePageUrl, 'x-default');
      } else {
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
      }

      const structuredDataId = 'page-structured-data';
      let structuredDataEl = document.getElementById(structuredDataId);
      if (structuredData) {
        if (!structuredDataEl) {
          structuredDataEl = document.createElement('script');
          structuredDataEl.id = structuredDataId;
          structuredDataEl.type = 'application/ld+json';
          document.head.appendChild(structuredDataEl);
        }
        structuredDataEl.textContent = JSON.stringify(structuredData);
      } else if (structuredDataEl) {
        structuredDataEl.remove();
      }
    };

    updateSEO();
    i18n.on('languageChanged', updateSEO);

    return () => {
      i18n.off('languageChanged', updateSEO);
      document.documentElement.removeAttribute('data-page-seo-active');
    };
  }, [title, description, keywords, noIndex, canonicalPath, image, type, structuredData, defer, settings, i18n]);
};

export default usePageSEO;
